import {
  ApeironInfinityDataGrid,
  ApeironInfinityDataGridRef,
  InfinityDatasourceResult,
  NextLink
} from '@apeiron/temp'
import { Box, styled, Typography, useMediaQuery } from '@mui/material'
import OrbitalTrackDetachPlanetButton from '@src/components/orbitalTrack/detail/DetachPlanetButton'
import { ROUTE } from '@src/constants/route'
import { useOrbitalTrackAttachContext } from '@src/contexts/orbitalTrack/OrbitalTrackAttachContext'
import useOrbitalTrackHelper from '@src/hooks/apeiron/orbitalTrack/useOrbitalTrackHelper'
import useGetOrbitalTrackDetail from '@src/hooks/graphql/useGetOrbitalTrackDetail'
import theme from '@src/styles/theme'
import { OrbitalTrackLite } from '@src/types/orbitalTrack'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { differenceInDays } from 'date-fns'
import queryString from 'query-string'
import * as R from 'ramda'
import { FC, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'

const StyledDataGrid = styled(ApeironInfinityDataGrid)`
  .Custom-Grid {
    border-radius: 0px;

    .ag-header {
      color: white;
      font-weight: 400;
      margin-bottom: -1px;
    }
    .ag-header-row {
      background-color: #151615;
    }
    .ag-cell {
      color: white;
      background-color: #0e0e12;
      border-bottom: 1px solid #232326;
    }
  }
`

const StyledOrbitalTrackDetachPlanetButton = styled(
  OrbitalTrackDetachPlanetButton
)`
  border-radius: 4px;
  ${props => props.theme.breakpoints.up('res1280')} {
    width: 134px;
  }
  ${props => props.theme.breakpoints.down('res1280')} {
    width: 100px;
  }
`

const MobileCell = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 10px;
  padding: 10px;
`

const MobileCellRow = styled(Box)`
  display: flex;
  justify-content: center;
  width: 100%;
`

const MobileCellSectionContainer = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: column;
  flex: 1;
  font-size: 14px;
  font-weight: 400;
  gap: 5px;
  line-height: 14px;
`

const MobileCellSectionTitle = styled(Typography)`
  color: #c4c4c4;
  font-size: 12px;
  font-weight: 300;
  line-height: 12px;
`

const PlanetLink = styled(NextLink)`
  color: white;
  :hover {
    color: #ffbf36;
  }
`

const MobileCellSection: FC<MobileCellProps> = (props: MobileCellProps) => {
  const { className, children, title } = props

  return (
    <MobileCellSectionContainer className={className}>
      {!R.isNil(title) && (
        <MobileCellSectionTitle>{title}</MobileCellSectionTitle>
      )}
      {children}
    </MobileCellSectionContainer>
  )
}

const PlanetOnTrackDatagrid: FC<Props> = props => {
  const { t } = useTranslation()

  const ref = useRef<ApeironInfinityDataGridRef | null>(null)

  const { className } = props

  const mobile = useMediaQuery(theme.breakpoints.down('res1024'))

  const { checkInCooldown } = useOrbitalTrackHelper()

  const { star, refetchStar } = useOrbitalTrackAttachContext()

  // TODO: should fetch my orbitals and using paging
  const { fetch: fetchMyOrbitals } = useGetOrbitalTrackDetail()

  const handleOnFetch =
    useCallback(async (): Promise<InfinityDatasourceResult> => {
      if (!R.isNil(star)) {
        const result = await fetchMyOrbitals(star.id)

        // remove tracks in cooldown
        const data = R.reject(
          (track: OrbitalTrackLite) => checkInCooldown(track),
          R.pathOr([], ['data', 'ownerTracks'], result)
        )

        return {
          data,
          total: R.length(data)
        }
      }

      return {
        data: [],
        total: 0
      }
    }, [fetchMyOrbitals, star])

  const handleOnDetachSuccess = useCallback(() => {
    refetchStar()
  }, [refetchStar])

  const columns = useMemo((): ColDef<any, OrbitalTrackLite>[] => {
    return mobile
      ? [
          {
            cellClass: 'custom-ag-cell-center',
            field: 'mobileCell',
            flex: 1,
            headerClass: 'custom-ag-header-center',
            cellRenderer: (params: ICellRendererParams<OrbitalTrackLite>) => {
              const planetUrl = queryString.stringifyUrl({
                url: ROUTE.MARKETPLACE_PLANET_DETAIL,
                query: {
                  planetId: params.data?.planetId
                }
              })

              return !R.isNil(params.data) ? (
                <MobileCell>
                  <MobileCellRow>
                    <MobileCellSection title={t('orbit.list.grid.planet')}>
                      <PlanetLink href={planetUrl}>
                        <Trans>{params.data?.planet?.name}</Trans>
                      </PlanetLink>
                    </MobileCellSection>
                    {/* don't remove this blank <MobileCellSection> */}
                    <MobileCellSection title={t('orbit.list.grid.age')}>
                      <Trans>
                        {t('common.i18n_format.year_old_other', {
                          count: Math.trunc(params.data.planet?.age || 0)
                        })}
                      </Trans>
                    </MobileCellSection>
                  </MobileCellRow>
                  <MobileCellRow>
                    <MobileCellSection
                      title={t('orbit.list.tab.orbiting_days')}
                    >
                      <Trans>
                        {t('godiverse.orbital_track.grid.row.day', {
                          day: differenceInDays(
                            new Date(),
                            new Date(params.data.rentalStartTime || 0)
                          )
                        })}
                      </Trans>
                    </MobileCellSection>
                    <MobileCellSection>
                      <StyledOrbitalTrackDetachPlanetButton
                        track={params.data}
                        onSuccess={handleOnDetachSuccess}
                      />
                    </MobileCellSection>
                  </MobileCellRow>
                </MobileCell>
              ) : (
                <></>
              )
            }
          }
        ]
      : [
          {
            cellClass: 'custom-ag-cell-center',
            field: 'planet',
            flex: 1,
            headerName: t('orbit.list.grid.planet'),
            headerClass: 'custom-ag-header-center',
            cellRenderer: (params: ICellRendererParams<OrbitalTrackLite>) => {
              const planetUrl = queryString.stringifyUrl({
                url: ROUTE.MARKETPLACE_PLANET_DETAIL,
                query: {
                  planetId: params.data?.planetId
                }
              })

              return !R.isNil(params.data) ? (
                <PlanetLink href={planetUrl}>
                  <Trans>{params.data?.planet?.name}</Trans>
                </PlanetLink>
              ) : (
                <></>
              )
            }
          },
          {
            cellClass: 'custom-ag-cell-center',
            field: 'age',
            flex: 1,
            headerClass: 'custom-ag-header-center',
            headerName: t('orbit.list.grid.age'),
            valueGetter: 'node.id',
            cellRenderer: (params: ICellRendererParams<OrbitalTrackLite>) => {
              return !R.isNil(params.data) ? (
                <Trans>
                  {t('common.i18n_format.year_old_other', {
                    count: Math.trunc(params.data.planet?.age || 0)
                  })}
                </Trans>
              ) : (
                <></>
              )
            }
          },
          {
            cellClass: 'custom-ag-cell-center',
            field: 'dayOrbiting',
            flex: 1,
            headerClass: 'custom-ag-header-center',
            headerName: t('orbit.list.tab.orbiting_days'),
            valueGetter: 'node.id',
            cellRenderer: (params: ICellRendererParams<OrbitalTrackLite>) => {
              return !R.isNil(params.data) ? (
                <Trans>
                  {t('godiverse.orbital_track.grid.row.day', {
                    day: differenceInDays(
                      new Date(),
                      new Date(params.data.rentalStartTime || 0)
                    )
                  })}
                </Trans>
              ) : (
                <></>
              )
            }
          },
          {
            cellClass: 'custom-ag-cell-center',
            field: 'action',
            flex: 1,
            headerClass: 'custom-ag-header-center',
            headerName: t('orbit.list.grid.action'),
            valueGetter: 'node.id',
            cellRenderer: (params: ICellRendererParams<OrbitalTrackLite>) => {
              return !R.isNil(params.data) ? (
                <StyledOrbitalTrackDetachPlanetButton
                  track={params.data}
                  onSuccess={handleOnDetachSuccess}
                />
              ) : (
                <></>
              )
            }
          }
        ]
  }, [mobile, handleOnDetachSuccess, t])

  useEffect(() => {
    if (ref && ref.current) {
      ref.current.refreshData()
    }
  }, [star])

  return (
    <StyledDataGrid
      className={className}
      columnDefs={columns}
      gridRef={ref}
      pageSize={20}
      fetch={handleOnFetch}
      {...(mobile
        ? { headerHeight: 0, rowHeight: 110 }
        : { headerHeight: 40, rowHeight: 60 })}
    />
  )
}

type MobileCellProps = {
  children?: ReactNode
  className?: string
  title?: string
}

type Props = {
  className?: string
}

export default PlanetOnTrackDatagrid
